/**
 * /checkout/$plan — Intent-preserving Stripe Checkout entry point.
 *
 * Users land here AFTER any required auth round-trip (Pro CTA on /pricing
 * → /auth/login?returnUrl=/checkout/pro → magic-link or Google OAuth →
 * back here, now authenticated). On mount we fire `subscription.upgrade()`
 * which returns a Stripe Checkout URL and we redirect the browser to it.
 *
 * Why a route instead of a button: the user's intent ("pay for Pro") must
 * survive the sign-in round-trip. A URL is the most reliable carrier of
 * intent across an auth redirect.
 */
import { track } from '@app/analytics/client';
import { useUpgrade } from '@app/features/billing';
import type { PlanName } from '@app/billing-config';
import { PLAN_LABELS } from '@app/billing-config';
import { Button, Card, CardContent, CardDescription, CardHeader, CardTitle } from '@app/ui';
import { createFileRoute, Link, redirect } from '@tanstack/react-router';
import { useEffect } from 'react';

// Plans wired into the Better-Auth Stripe plugin in
// packages/auth/src/config.ts. University tiers are NOT in this set —
// they use metered billing via direct Stripe API, not subscription.upgrade(),
// so /checkout/university-* would fail at the plugin's plan-lookup step.
// Anything not in this set falls through to the redirect to /pricing.
const ALLOWED_PLANS: ReadonlySet<PlanName> = new Set<PlanName>([
  'pro',
  'agent-solo',
  'agent-team',
]);

function isPaidPlan(value: string): value is PlanName {
  return ALLOWED_PLANS.has(value as PlanName);
}

export const Route = createFileRoute('/_authenticated/checkout/$plan')({
  validateSearch: (search: Record<string, unknown>) => ({
    annual: search.annual === 'true' || search.annual === true,
  }),
  beforeLoad: ({ params }) => {
    if (!isPaidPlan(params.plan)) {
      throw redirect({ to: '/pricing' });
    }
  },
  component: CheckoutRedirect,
});

function CheckoutRedirect() {
  const { plan } = Route.useParams();
  const { annual } = Route.useSearch();
  const planName = plan as PlanName;

  const { upgrade, loading, error } = useUpgrade({ plan: planName, annual });

  useEffect(() => {
    track('checkout_started', { plan: planName, annual, current_plan: null });
    upgrade();
  }, [upgrade, planName, annual]);

  return (
    <div className="min-h-[60vh] flex items-center justify-center p-6">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle>
            {error ? 'Checkout failed' : `Starting ${PLAN_LABELS[planName]} checkout`}
          </CardTitle>
          <CardDescription>
            {error
              ? "We couldn't start your checkout session."
              : 'Redirecting you to Stripe to complete payment…'}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {error ? (
            <>
              <div className="p-3 rounded-md bg-destructive/10 border border-destructive/50 text-destructive text-sm">
                {error}
              </div>
              <div className="flex flex-col gap-2">
                <Button onClick={upgrade} disabled={loading}>
                  {loading ? 'Retrying…' : 'Try again'}
                </Button>
                <Link to="/pricing">
                  <Button variant="outline" className="w-full">
                    Back to pricing
                  </Button>
                </Link>
              </div>
            </>
          ) : (
            <p className="text-sm text-muted-foreground text-center">
              If you aren't redirected within a few seconds,{' '}
              <Button
                type="button"
                variant="link"
                onClick={upgrade}
                disabled={loading}
                className="h-auto p-0 align-baseline"
              >
                click here
              </Button>
              .
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
